import type { Node, XYPosition } from '@xyflow/react'
import type { AgentSettingsFormValues } from '../config/base.config'
import { type ConversationGoal, FlowNodeTypes, type NodeDataBase } from '../types'

/**
 * Removes position, zIndex, measured, and selected fields from nodes
 * Also removes sensitive fields from:
 * - Start nodes: auth_token, auth_id, require_http_auth, slack_bot_token, slack_signing_secret
 * - HTTP_REQUEST nodes: auth_params.username, auth_params.password, auth_params.token
 * Used when copying/exporting nodes to avoid including UI-specific properties and sensitive data
 */
export const cleanNodesForExport = <T extends Node<NodeDataBase<unknown>>>(
  nodes: (T & {
    position?: XYPosition
    zIndex?: number
    measured?: { width?: number; height?: number }
    selected?: boolean
  })[],
): Omit<T, 'position' | 'zIndex' | 'measured' | 'selected'>[] =>
  nodes.map((node) => {
    const { position: _, zIndex: __, measured: ___, selected: ____, ...cleanNode } = node

    // Remove sensitive fields from start nodes
    if (cleanNode.type === FlowNodeTypes.start && cleanNode.data?.config) {
      const {
        auth_token: _authToken,
        auth_id: _authId,
        require_http_auth: _requireHttpAuth,
        slack_bot_token: _slackBotToken,
        slack_signing_secret: _slackSigningSecret,
        ...configWithoutSensitiveFields
      } = cleanNode.data.config as Record<string, unknown>

      return {
        ...cleanNode,
        data: {
          ...cleanNode.data,
          config: configWithoutSensitiveFields,
        },
      } as Omit<T, 'position' | 'zIndex' | 'measured' | 'selected'>
    }

    // Strip auth credentials from HTTP_REQUEST nodes
    if (cleanNode.type === FlowNodeTypes.http_request && cleanNode.data?.config) {
      const config = cleanNode.data.config as Record<string, unknown>
      const httpRequest = config.http_request as Record<string, unknown> | undefined

      if (httpRequest?.auth_params) {
        return {
          ...cleanNode,
          data: {
            ...cleanNode.data,
            config: {
              ...config,
              http_request: {
                ...httpRequest,
                auth_params: {
                  ...(httpRequest.auth_params as Record<string, unknown>),
                  username: '',
                  password: '',
                  token: '',
                },
              },
            },
          },
        } as Omit<T, 'position' | 'zIndex' | 'measured' | 'selected'>
      }
    }

    return cleanNode
  })

export const cleanConversationGoalsForExport = (
  conversationGoals?: ConversationGoal[] | null,
): ConversationGoal[] =>
  (conversationGoals ?? []).map(({ goal_name, goal_instructions }) => ({
    goal_name,
    goal_instructions,
  }))

/**
 * Sanitizes agent settings event_callbacks by clearing auth credentials
 * (username, password, token) from each callback (HANGUP, RECORDING, ERROR)
 */
export const cleanAgentSettingsForExport = (
  agentSettings: AgentSettingsFormValues,
): AgentSettingsFormValues => {
  const cleanedAgentSettings = {
    ...agentSettings,
    ...(agentSettings.conversation_goals
      ? { conversation_goals: cleanConversationGoalsForExport(agentSettings.conversation_goals) }
      : {}),
  }

  if (!cleanedAgentSettings.event_callbacks) return cleanedAgentSettings

  const cleanedCallbacks = Object.fromEntries(
    Object.entries(cleanedAgentSettings.event_callbacks).map(([key, callback]) => [
      key,
      {
        ...callback,
        username: '',
        password: '',
        token: '',
      },
    ]),
  )

  return {
    ...cleanedAgentSettings,
    event_callbacks: cleanedCallbacks as AgentSettingsFormValues['event_callbacks'],
  }
}
